import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API_BASE } from "@/api/config";

/**
 * Landing page for the unsubscribe link at the bottom of product-update
 * emails. Public (no session): the token in the URL identifies the user.
 * The server answers 400 for anything it cannot honour, without saying why.
 */
export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"working" | "success" | "error">("working");
  const calledRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      return;
    }

    // StrictMode mounts effects twice in dev — one request is enough.
    if (calledRef.current) return;
    calledRef.current = true;

    async function unsubscribe() {
      try {
        const response = await fetch(`${API_BASE}/auth/unsubscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        setStatus(response.ok ? "success" : "error");
      } catch {
        setStatus("error");
      }
    }

    unsubscribe();
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "working") {
    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        <p className="text-sm text-ink-3">Updating your preferences...</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ok/20">
          <svg className="h-7 w-7 text-ok" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-ink">You are unsubscribed from product updates.</h2>
        <p className="text-sm text-ink-3">
          Account and billing emails still reach you.{" "}
          <Link to="/login" className="text-ink-2 transition-colors hover:text-ink">
            Changed your mind? Sign in and write to us from Help.
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-bad/20">
        <svg className="h-7 w-7 text-bad" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-ink">This link is invalid or has expired.</h2>
      <p className="text-sm text-ink-3">
        <Link to="/login" className="text-ink-2 transition-colors hover:text-ink">
          Sign in
        </Link>{" "}
        and write to us from Help if you would like to stop receiving product updates.
      </p>
    </div>
  );
}
